import { httpsCallable } from 'firebase/functions';
import { getFns } from '../firebase/functions';
import type { SerializedPattern } from '../persistence/serialize';
import type { AnySizeKey } from './sizes';
import type { StyleKey } from './presets';

/**
 * Client de la Cloud Function `createDraftOrder` (Lot P4 — CDC §5.2).
 *
 * RÈGLE NON NÉGOCIABLE : aucun montant n'est envoyé — le serveur RECALCULE
 * le prix à partir du patron (anti-fraude). Le client ne transmet que la
 * configuration ; il reçoit l'URL du checkout Shopify hébergé (invoiceUrl)
 * et y redirige.
 */

/** Rangement des perles : un sachet par couleur, ou un sachet par plaque. */
export type Packaging = 'color' | 'plate';

export type CheckoutRequest = {
  pattern: SerializedPattern;
  sizeKey: AnySizeKey;
  styleKey: StyleKey;
  /** Position du slider Détails (0-100) — traçabilité de la config. */
  detail: number;
  /** Rangement des perles choisi par le client (défaut serveur : 'color'). */
  packaging?: Packaging;
  /** E-mail optionnel : pré-remplit le contact au checkout Shopify. */
  email?: string;
};

export type CheckoutResponse = {
  invoiceUrl: string;
  kitId: string;
  /** Prix calculé côté serveur, en centimes (affichage de confirmation). */
  priceCents: number;
};

/**
 * Clé d'IDEMPOTENCE de la tentative de commande en cours : générée au premier
 * appel, CONSERVÉE tant que le serveur n'a pas répondu avec une invoiceUrl.
 * Un retry (réseau, double-clic) renvoie la même clé → le serveur rejoue la
 * réponse au lieu de créer un second Draft Order. Réinitialisée au succès.
 */
let pendingIdempotencyKey: string | null = null;

export async function createDraftOrder(request: CheckoutRequest): Promise<CheckoutResponse> {
  const fns = getFns();
  if (!fns) {
    throw new Error(
      'Commande indisponible : configuration Firebase absente. Réessaie plus tard.',
    );
  }
  pendingIdempotencyKey ??= crypto.randomUUID();
  const callable = httpsCallable<CheckoutRequest & { idempotencyKey: string }, CheckoutResponse>(
    fns,
    'createDraftOrder',
  );
  const result = await callable({ ...request, idempotencyKey: pendingIdempotencyKey });
  const data = result.data;
  if (!data || typeof data.invoiceUrl !== 'string' || data.invoiceUrl.length === 0) {
    throw new Error('Réponse de commande invalide.');
  }
  pendingIdempotencyKey = null; // commande aboutie → la prochaine repart à neuf
  return data;
}
